/**
 * Which reporting period the whole application is looking at.
 *
 * Deliberately global, and deliberately *not* persisted.
 *
 * Global, because every screen has to agree. A dashboard showing November beside a register
 * grid still showing December is not a bug anyone reports — it is a number someone quotes in
 * a meeting, and nobody finds out for a month.
 *
 * Not persisted, because the open period is the one people work in and time travel is an
 * errand. Someone who looked at March last Tuesday should not open the product on Wednesday
 * still in March, be unable to edit anything, and conclude the product is broken. Preferences
 * survive a reload; where you were looking does not.
 *
 * `null` means the open period, resolved by the API rather than named here — the client does
 * not get to decide which month is open.
 */
import { create } from 'zustand'

type ViewPeriodState = {
  code: string | null
  setCode: (code: string | null) => void
}

export const useViewPeriod = create<ViewPeriodState>((set) => ({
  code: null,
  setCode: (code) => set({ code }),
}))

/**
 * The period to send with a read, as a query fragment.
 *
 * Returns an empty string for the open period so callers can concatenate without a
 * conditional, and so the URL of a normal request stays clean.
 */
export function periodParam(code: string | null): string {
  return code ? `&period=${encodeURIComponent(code)}` : ''
}

/** The same, as the leading parameter. */
export function periodQuery(code: string | null): string {
  return code ? `?period=${encodeURIComponent(code)}` : ''
}
